"use client"

import { useState } from "react"
import { FieldPath, FieldValues, PathValue, useFormContext } from "react-hook-form"
import {
  decideWorkScopeStatusChange,
  localCalendarDate,
  WORK_SCOPE_COMPLETE_PHOTO_WARNING,
} from "../lib/work-scope-complete"
import {
  useResolvedWorkScopeId,
  useWorkScopeCompletePhotosContext,
} from "../context/work-scope-complete-photos-context"

export function useWorkScopeCompletion<
  TFieldValues extends FieldValues = FieldValues,
>(statusFieldName: FieldPath<TFieldValues>, workScopeSlug: string) {
  const context = useWorkScopeCompletePhotosContext()
  const workScopeId = useResolvedWorkScopeId(workScopeSlug)
  const { getValues, setValue } = useFormContext<TFieldValues>()
  const [warning, setWarning] = useState<string | null>(null)

  const dateFieldName = `${statusFieldName}DateCompleted` as FieldPath<TFieldValues>
  const photo = workScopeId == null
    ? null
    : context?.photosByWorkScopeId[String(workScopeId)] ?? null

  const onUserChange = (nextStatus: string): boolean => {
    if (!context) return true
    if (!context.isReady) return true

    const completeTakenAt = photo?.photoId != null ? photo.takenAt : null
    const decision = decideWorkScopeStatusChange({
      nextStatus,
      currentDate: getValues(dateFieldName) as string | null | undefined,
      takenAt: completeTakenAt,
      today: localCalendarDate(),
    })

    if (!decision.allow) {
      setWarning(decision.warning || WORK_SCOPE_COMPLETE_PHOTO_WARNING)
      return false
    }

    if ("nextDate" in decision) {
      setValue(dateFieldName, decision.nextDate as PathValue<TFieldValues, FieldPath<TFieldValues>>, {
        shouldDirty: true,
        shouldTouch: true,
      })
    }
    setWarning(null)
    return true
  }

  return {
    dateFieldName,
    photo,
    warning,
    onUserChange,
    enabled: context != null,
    isReady: context?.isReady === true,
  }
}
